import {Component, computed, effect, OnInit} from '@angular/core';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {Checkbox} from 'primeng/checkbox';
import {FormsModule} from '@angular/forms';
import {AutoComplete, AutoCompleteCompleteEvent} from 'primeng/autocomplete';
import {TravellerDetails, UserType} from '../../../../api';
import {getUserTypeSuffix} from '../../../../util/general.utils';
import {fuzzySearch} from '../../../../util/search.utils';
import {CreateTemplateBaseComponent} from '../create-template-base-component.directive';

/**
 * Template version of create-ta-travellers. No Non-CLC Staff section, and pre-payment is shown
 * disabled: a template stores who is travelling and whether each traveller will acquit.
 */
@Component({
  selector: 'create-template-travellers',
  imports: [
    Button,
    Card,
    Checkbox,
    FormsModule,
    AutoComplete,
  ],
  templateUrl: './create-template-travellers.html',
})
export class CreateTemplateTravellers extends CreateTemplateBaseComponent implements OnInit {
  emptyTraveller: TravellerDetails | undefined;
  availableTravellers = computed<TravellerDetails[]>(() => (
    this.store.availableUsers().map(user => ({
      details: user,
      authorisedAdjustmentDate: null,
      isSelfAcquit: false
    }))
  ));
  filteredTravellers: TravellerDetails[] = [];
  protected readonly UserType = UserType;
  protected readonly getUserTypeSuffix = getUserTypeSuffix;

  constructor() {
    super();

    effect(() => {
      if (!this.isReadOnly()) {
        this.addAcquit(this.template()?.travellers ?? []);
      }
    });
  }

  ngOnInit(): void {
    this.taSearchDatasource.loadTravellerOptions();
  }

  isPrimary(traveller: TravellerDetails): boolean {
    return !!traveller.details?.id && traveller.details.id === this.template()?.primaryTraveller?.id
      && traveller.details.userType !== UserType.Casual;
  }

  // The server makes the first staff traveller primary when there is none.
  addStaffTraveller(traveller: TravellerDetails): void {
    this.templateDatasource.saveTravellers([...(this.template()?.travellers ?? []), traveller]);
    this.emptyTraveller = undefined;
  }

  // The server hands the primary role on when its holder is removed.
  removeStaffTraveller(traveller: TravellerDetails): void {
    this.templateDatasource.saveTravellers((this.template()?.travellers ?? []).filter(t => t !== traveller));
  }

  // As on a TA: a lone traveller acquits unless they are a casual. Compared first to avoid a save loop.
  addAcquit(travellers: TravellerDetails[]): void {
    if (travellers.length === 1) {
      const desired = travellers[0].details?.userType !== UserType.Casual;
      if (!!travellers[0].isSelfAcquit !== desired) {
        this.templateDatasource.saveTravellers([{...travellers[0], isSelfAcquit: desired}]);
      }
    }
  }

  isAcquitDisabled(traveller: TravellerDetails): boolean {
    return this.template()?.travellers?.length === 1 || traveller.details?.userType === UserType.Casual;
  }

  updateTravellerAcquittal(acquit: boolean, traveller: TravellerDetails): void {
    this.templateDatasource.saveTravellers((this.template()?.travellers ?? []).map(t =>
      t === traveller ? {...t, isSelfAcquit: acquit} : t));
  }

  addPrimaryRole(traveller: TravellerDetails): void {
    // As on a self-raised TA: the new primary acquits.
    if (!traveller.isSelfAcquit) {
      this.updateTravellerAcquittal(true, traveller);
    }
    this.templateDatasource.saveField('primaryTraveller', traveller.details?.id, {primaryTraveller: traveller.details});
  }

  filterTravellers(event: AutoCompleteCompleteEvent): void {
    const travellers = this.template()?.travellers ?? [];
    const userIds = travellers.map(traveller => traveller.details?.id);
    // The first traveller becomes primary, which a casual cannot be.
    const isAddingPrimary = travellers.length === 0;
    const eligible = this.availableTravellers().filter(traveller =>
      !userIds.includes(traveller.details?.id) &&
      (!isAddingPrimary || traveller.details?.userType !== UserType.Casual)
    );
    this.filteredTravellers = fuzzySearch(eligible, event.query, t => this.travellerSearchText(t));
  }

  // Name only: email and initials are deliberately not searchable.
  private travellerSearchText(traveller: TravellerDetails): string {
    const details = traveller.details;
    return [details?.firstName, details?.surname].filter(Boolean).join(' ') || details?.name || '';
  }

  getTravellersFullName(traveller: TravellerDetails): string {
    if (traveller.details != null) {
      if (traveller.details?.firstName != null) {
        return traveller.details?.firstName + " " + (traveller.details?.surname ?? '');
      }
      if (traveller.details.name != null){
        return traveller.details.name;
      }
      return traveller.details!.email!;
    }
    return '';
  }
}
